import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type RecipeSummary } from '../api/endpoints';
import { ApiException } from '../api/client';
import { RecipeCard } from '../components/RecipeCard';
import { Skeleton, ErrorState, EmptyState } from '../components/states';

type Occasion = 'home' | 'work';

// «Праздничное» — лента праздничных блюд (зеркало CuisineScreen) с сегментом Дом/Работа.
// Дом — полный стол; Работа — что удобно принести и разделить с коллегами (бэкенд исключает супы).
// Смена режима меняет occasion → load пересоздаётся → перезапрос.
export function FestiveScreen() {
  const nav = useNavigate();
  const [occasion, setOccasion] = useState<Occasion>('home');
  const [items, setItems] = useState<RecipeSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    setItems(null);
    void endpoints.getFestiveRecipes(occasion).then(
      (r) => setItems(r.items),
      (e) => setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить'),
    );
  }, [occasion]);

  useEffect(() => {
    // Разовая загрузка при смене режима — намеренный fetch-in-effect (план без TanStack).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  return (
    <div className="px-4 pt-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} aria-label="Назад" className="text-xl text-ink/60">
          ←
        </button>
        <h1 className="text-2xl font-bold text-ink">Праздничное</h1>
      </div>

      <div role="tablist" aria-label="Где праздник" className="mb-3 flex gap-1 rounded-xl bg-black/5 p-1">
        {([
          ['home', 'Дом'],
          ['work', 'Работа'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            role="tab"
            aria-selected={occasion === value}
            onClick={() => setOccasion(value)}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition ${
              occasion === value ? 'bg-white text-ink shadow-sm' : 'text-ink/60'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {occasion === 'work' && (
        <p className="mb-4 text-sm text-ink/60">Что удобно принести и разделить</p>
      )}

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : items === null ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Пока нет блюд" hint="Скоро добавим праздничные блюда." />
      ) : (
        <div className="stagger space-y-4">
          {items.map((r) => (
            <RecipeCard
              key={r.id}
              title={r.title ?? ''}
              imageUrl={r.imageUrl}
              cookTimeMin={r.cookTimeMin}
              servings={r.servings}
              onClick={() => nav(`/recipe/${r.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
